/**
 * The vcs capability's bridge handler (RFC 0264 §4/§5). Lives WITH the capability,
 * not in core `appBridge`: the bridge just dispatches `vcs.*` here.
 *
 * mod.vcs reads are answered by the git server over HTTP, not locally — the host
 * (a browser) holds no git tree and the sandboxed view can't network, so the view
 * asks the host and the host fetches. The interface is stable across the eventual
 * git-over-subduction transport (RFC 0268 piece four); only this fetch changes.
 */

import { projectSources } from '@mod/core/model'
import type { BridgeContext } from './capability'
import { hasLocalServer, projectEndpoint } from './localServer'

const SHA = /^[0-9a-f]{40}$/

/**
 * The key for a read that can never change, or null.
 *
 * A read named by commit shas is the same forever: the file at a commit, one
 * commit's diff, the diff between two commits. Those are the reads a review
 * page repeats on every open, so they are answered from memory after the
 * first time. A read named by a branch is not: the branch can move, so it
 * always goes to the server.
 */
export function immutableKey(
  method: string,
  args: unknown[],
  projectId: string,
  sourceId?: string,
): string | null {
  const a = args.map((x) => String(x ?? ''))
  const pinned =
    (method === 'fileAt' && SHA.test(a[0] ?? '')) ||
    (method === 'commitDiff' && SHA.test(a[0] ?? '')) ||
    (method === 'diff' && SHA.test(a[0] ?? '') && SHA.test(a[1] ?? ''))
  return pinned ? JSON.stringify([projectId, sourceId, method, ...a]) : null
}

/** Answers by `immutableKey`, most recently used last, capped by size. */
const answers = new Map<string, { value: unknown; size: number }>()
let answersSize = 0
/** About 64 MB of JSON: a large review's files several times over. */
export const VCS_CACHE_BYTES = 64 * 1024 * 1024

/**
 * Roughly how much room an answer takes, without serialising it.
 *
 * `JSON.stringify(value).length` measures this exactly, but it builds a string
 * as large as the answer and then throws it away: on a review page that is a
 * whole file's contents serialised again per read, on the window's one thread.
 * Walking the value allocates nothing, and the walk stops once the total
 * passes the cap, because an answer above it is refused anyway.
 */
export function roughSize(value: unknown, limit = VCS_CACHE_BYTES): number {
  let total = 0
  const visit = (v: unknown, depth: number): void => {
    // A value off the server is plain JSON, so the depth guard is only there to
    // keep a surprise cycle from hanging the walk.
    if (total > limit || depth > 32) return
    if (typeof v === 'string') {
      total += v.length + 2
      return
    }
    if (v === null || typeof v !== 'object') {
      total += 8
      return
    }
    total += 2
    if (Array.isArray(v)) {
      for (const item of v) {
        if (total > limit) return
        visit(item, depth + 1)
      }
      return
    }
    // `Object.keys` allocates the key list, which is small beside the answer
    // itself: the point of this walk is to not build a copy of the value.
    for (const k of Object.keys(v)) {
      if (total > limit) return
      total += k.length + 4
      visit((v as Record<string, unknown>)[k], depth + 1)
    }
  }
  visit(value, 0)
  return total
}

function remember(key: string, value: unknown): void {
  const size = roughSize(value)
  if (size > VCS_CACHE_BYTES) return
  const prev = answers.get(key)
  if (prev) answersSize -= prev.size
  answers.delete(key)
  answers.set(key, { value, size })
  answersSize += size
  for (const [k, v] of answers) {
    if (answersSize <= VCS_CACHE_BYTES) break
    answers.delete(k)
    answersSize -= v.size
  }
}

/** Test seam: forget every cached answer. */
export function clearVcsCache(): void {
  answers.clear()
  answersSize = 0
}

/**
 * Fetch the git server's HTTP API and return the mapped result (FileDiff[] for
 * diff, etc.). A plain request/response, nothing on the relay. `base` is
 * selectable per call (defaults to the project trunk server-side).
 *
 * A read named by commit shas is answered from memory after the first time
 * (`immutableKey`), so a pane reopened on the same review reads nothing again.
 * The memory is the window's: it lasts until the window reloads.
 */
export async function fetchVcsFromServer(
  method: string,
  args: unknown[],
  /** Whose checkout: the path on the daemon's one server (RFC 0331 3a). */
  projectId: string,
  sourceId?: string,
): Promise<unknown> {
  const key = immutableKey(method, args, projectId, sourceId)
  const hit = key ? answers.get(key) : undefined
  if (key && hit) {
    // Most recently used last, so the cap drops the oldest reads first.
    answers.delete(key)
    answers.set(key, hit)
    return hit.value
  }
  const value = await fetchFromServer(method, args, projectId, sourceId)
  if (key) remember(key, value)
  return value
}

async function fetchFromServer(
  method: string,
  args: unknown[],
  projectId: string,
  sourceId?: string,
): Promise<unknown> {
  let path: string
  let post: Record<string, unknown> | undefined
  if (method === 'branches') {
    const base = args[0] ? `?base=${encodeURIComponent(String(args[0]))}` : ''
    path = `/branches${base}`
  } else if (method === 'diff') {
    const base = args[1] ? `&base=${encodeURIComponent(String(args[1]))}` : ''
    path = `/diff?branch=${encodeURIComponent(String(args[0] ?? ''))}${base}`
  } else if (method === 'commits') {
    // The commits on a branch, not its merged diff. `base` is what they are
    // counted against, so it is passed the same way `diff` passes it.
    const base = args[1] ? `&base=${encodeURIComponent(String(args[1]))}` : ''
    const limit = args[2] ? `&limit=${encodeURIComponent(String(args[2]))}` : ''
    path = `/commits?branch=${encodeURIComponent(String(args[0] ?? ''))}${base}${limit}`
  } else if (method === 'log') {
    // The checkout's recent commits across its branches (RFC 0313 3i). Not a
    // sha read, so never answered from memory: the next commit must show.
    const q = new URLSearchParams()
    if (args[0]) q.set('days', String(args[0]))
    if (args[1]) q.set('limit', String(args[1]))
    path = `/log?${q.toString()}`
  } else if (method === 'fileLog') {
    // The commits that changed one file (RFC 0313 3g). Not pinned to a sha,
    // so never answered from memory.
    const q = new URLSearchParams({ path: String(args[0] ?? '') })
    if (args[1]) q.set('ref', String(args[1]))
    path = `/fileLog?${q.toString()}`
  } else if (method === 'commitDiff') {
    // One revision. It takes no base: the server diffs it against its own first
    // parent, which is the only comparison "what did this commit change" means.
    path = `/commitDiff?sha=${encodeURIComponent(String(args[0] ?? ''))}`
  } else if (method === 'working') {
    // No arguments by construction: the working tree is not a ref, so there is
    // nothing to name and nothing to validate.
    path = '/working'
  } else if (method === 'workingFile') {
    // A branch reads the file from the worktree that has it checked out
    // (RFC 0329 3b); without one, this checkout.
    const q = new URLSearchParams({ path: String(args[0] ?? '') })
    if (args[1]) q.set('branch', String(args[1]))
    path = `/workingFile?${q.toString()}`
  } else if (method === 'list') {
    // One directory of the project's files (RFC 0277 S6, S8). A missing ref
    // is the working tree; a ref is read from the commit, never checked out.
    // A `~branch` ref is that branch's worktree on disk (RFC 0277 S11), listed
    // with its uncommitted files rather than read from a commit.
    const q = new URLSearchParams({ dir: String(args[0] ?? '') })
    const ref = args[1] ? String(args[1]) : ''
    if (ref.startsWith('~')) q.set('branch', ref.slice(1))
    else if (ref) q.set('ref', ref)
    path = `/list?${q.toString()}`
  } else if (method === 'changed') {
    // What a code pane's ref changed, with counts (RFC 0354 3b). The ref is
    // the pane's own, `~branch` included; the server reads which checkout.
    const q = new URLSearchParams({ ref: String(args[0] ?? '') })
    if (args[1]) q.set('base', String(args[1]))
    path = `/changed?${q.toString()}`
  } else if (method === 'files') {
    // Every path at a ref, for Go to file (RFC 0354 3d). Addressed like list.
    const q = new URLSearchParams()
    const ref = args[0] ? String(args[0]) : ''
    if (ref.startsWith('~')) q.set('branch', ref.slice(1))
    else if (ref) q.set('ref', ref)
    const qs = q.toString()
    path = qs ? `/files?${qs}` : '/files'
  } else if (method === 'addWorktree') {
    // `check out here` (RFC 0277 S4): a worktree on this device for a branch,
    // fetched from the remote if this device has never seen it.
    path = '/worktrees/add'
    post = {
      branch: String(args[0] ?? ''),
      // S14: a new branch started from this one.
      ...(args[1] ? { from: String(args[1]) } : {}),
    }
  } else if (method === 'worktrees') {
    // The project's checkouts on this device (RFC 0277 S11).
    path = '/worktrees'
  } else if (method === 'fileAt') {
    path = `/fileAt?ref=${encodeURIComponent(String(args[0] ?? ''))}&path=${encodeURIComponent(String(args[1] ?? ''))}`
  } else if (method === 'status') {
    // Ahead/behind + how current the base ref is (RFC 0264 §5). A review reads
    // this to say whether the diff beside it was computed against a base that
    // has since moved, which is the difference a local checkout has from a
    // pull request and the one a reviewer cannot otherwise see.
    const base = args[1] ? `&base=${encodeURIComponent(String(args[1]))}` : ''
    path = `/status?branch=${encodeURIComponent(String(args[0] ?? ''))}${base}`
  } else if (method === 'pr') {
    path = `/pr?branch=${encodeURIComponent(String(args[0] ?? ''))}`
  } else if (method === 'prs') {
    // The repo's pull requests, not one branch's. Open by default, so a picker
    // shows work in flight rather than everything that ever landed.
    const o = (args[0] ?? {}) as Record<string, unknown>
    const q = new URLSearchParams()
    if (o.state) q.set('state', String(o.state))
    if (o.limit) q.set('limit', String(o.limit))
    const qs = q.toString()
    path = qs ? `/prs?${qs}` : '/prs'
  } else if (method === 'tracked') {
    // Whether git holds a previous version of a file (RFC 0277 3f), asked
    // before the first save so the window can warn that it cannot be undone.
    const q = new URLSearchParams({ path: String(args[0] ?? '') })
    if (args[1]) q.set('branch', String(args[1]))
    path = `/tracked?${q.toString()}`
  } else if (method === 'save') {
    // RFC 0277 3e: a file written back, refused unless `readAt` is still the
    // hash of what is on disk. A refusal is an ANSWER ({ ok: false }), not a
    // failed request, so the caller can show the difference.
    const o = (args[0] ?? {}) as Record<string, unknown>
    path = '/save'
    post = {
      path: String(o.path ?? ''),
      content: String(o.content ?? ''),
      readAt: String(o.readAt ?? ''),
      ...(o.branch ? { branch: String(o.branch) } : {}),
    }
  } else if (method === 'remove') {
    // RFC 0357 S16: a checkout file that became a document leaves the disk,
    // against the hash of what was read. A refusal is an answer, as a save's.
    const o = (args[0] ?? {}) as Record<string, unknown>
    path = '/remove'
    post = {
      path: String(o.path ?? ''),
      readAt: String(o.readAt ?? ''),
      ...(o.branch ? { branch: String(o.branch) } : {}),
    }
  } else if (method === 'openPr' || method === 'mergePr') {
    // The only mod.vcs calls that CHANGE anything, and the reason this function
    // grew a POST. The server refuses them from a non-local origin, because
    // CORS stops a response being read, not a request being sent.
    const o = (args[0] ?? {}) as Record<string, unknown>
    path = method === 'openPr' ? '/pr/create' : '/pr/merge'
    post =
      method === 'openPr'
        ? {
            branch: String(o.branch ?? ''),
            base: String(o.base ?? ''),
            title: String(o.title ?? ''),
            body: String(o.body ?? ''),
            draft: o.draft === true,
          }
        : {
            branch: String(o.branch ?? ''),
            method: String(o.method ?? 'merge'),
            // The head the review's verdict was written against (RFC 0285 §3).
            // Passed straight through rather than resolved here: the value of
            // the check is that this number comes from the record the reviewer
            // read, not from anything asked for at merge time.
            expectedHead: String(o.expectedHead ?? ''),
          }
  } else {
    throw new Error(`unknown mod.vcs method: ${method}`)
  }
  const res = await fetch(
    `${projectEndpoint(projectId, 'git', sourceId)}${path}`,
    post
      ? {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(post),
        }
      : undefined,
  )
  if (!res.ok) {
    // The server returns 400 with an {error} body for a bad ref; surface it.
    let detail = `git server ${res.status}`
    try {
      const body = (await res.json()) as { error?: string }
      if (body?.error) detail = body.error
    } catch {
      // non-JSON body; keep the status.
    }
    throw new Error(`mod.vcs.${method}: ${detail}`)
  }
  return res.json()
}

/**
 * Handle `mod.vcs.*`. Two gates: the app's "vcs" READ grant (permission), AND the
 * project actually having vcs INSTALLED — a `VcsDoc`, written by `mod init` on a
 * repo (capability presence). A docs-only project has none, so mod.vcs is
 * unavailable there rather than hitting a server for code that isn't there.
 */
/** Per project source: does the daemon serve it as a git checkout? Asked
 *  through `/branches`, which a plain folder's handler does not answer. A
 *  refusal is not remembered, so a folder that becomes git is seen. */
const gitSources = new Map<string, Promise<boolean>>()
function servesGit(projectId: string, sourceId: string): Promise<boolean> {
  if (!hasLocalServer()) return Promise.resolve(false)
  const key = `${projectId}\u0000${sourceId}`
  let known = gitSources.get(key)
  if (!known) {
    known = fetchFromServer('branches', [], projectId, sourceId).then(
      () => true,
      () => false,
    )
    gitSources.set(key, known)
    void known.then((git) => {
      if (!git) gitSources.delete(key)
    })
  }
  return known
}

export async function vcsHandler(
  method: string,
  args: unknown[],
  ctx: BridgeContext,
): Promise<unknown> {
  // A write takes the separate grant: an app that can read a diff is not
  // thereby allowed to change a checkout, create a worktree, or land a PR.
  const isWrite = [
    'vcs.openPr',
    'vcs.mergePr',
    'vcs.save',
    'vcs.remove',
    'vcs.addWorktree',
  ].includes(method)
  const canWrite = ctx.writeGrants.includes('vcs')
  if (isWrite && !canWrite) {
    throw new Error('this app is not granted "vcs" write access')
  }
  if (!isWrite && !canWrite && !ctx.grants.includes('vcs')) {
    throw new Error('this app is not granted "vcs" access')
  }
  const catalog = projectSources(ctx.ws.doc())
  if (method === 'vcs.sources') {
    return catalog
  }
  const selected = args[0] as { sourceId?: unknown } | undefined
  const explicit =
    selected && typeof selected === 'object' && selected.sourceId != null
      ? String(selected.sourceId)
      : null
  // Existing apps have always called mod.vcs without a source. Keep those
  // calls on the project's default Git source when more sources are attached.
  const sourceId = explicit ?? catalog.defaultSourceId
  const source = catalog.sources.find((s) => s.id === sourceId)
  if (!source) throw new Error(`Git is unavailable for source ${sourceId}`)
  // A folder source with no VCS record (`mod init`) is often still a git
  // checkout, which the daemon serves as one. A plain folder it serves with a
  // file handler instead, so forwarding it blindly would hand an app with only
  // the vcs grant every file in the folder. Ask once whether it is git.
  if (!source.vcsDocId && !(await servesGit(ctx.projectId, sourceId))) {
    throw new Error(`Git is unavailable for source ${sourceId}`)
  }
  return fetchVcsFromServer(
    method.slice(4),
    explicit ? args.slice(1) : args,
    ctx.projectId,
    sourceId,
  )
}
